import { useNavigate } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { Avatar, Skeleton } from '@/components/ui';
import { ROUTES } from '@/config/constants';
import { recordCommunityVisit } from '../recentCommunities';
import { useRecentCommunities } from '../hooks/useMyCommunities';

interface RecentlyViewedRailProps {
  /** Opens the create-community modal (the trailing "+ New community" tile). */
  onCreate: () => void;
}

/**
 * "Jump back in" — a horizontal strip of recently-opened communities (driven by
 * device-local view history), capped with a "New community" tile. Mirrors the
 * home page's curated rail header.
 */
export function RecentlyViewedRail({ onCreate }: RecentlyViewedRailProps) {
  const navigate = useNavigate();
  const { communities, isLoading } = useRecentCommunities();

  const open = (id: string) => {
    recordCommunityVisit(id);
    navigate(ROUTES.community(id));
  };

  return (
    <section className="mb-7">
      <h3 className="mb-3 text-lg font-extrabold tracking-[-0.3px] text-white">Jump back in</h3>

      <div className="flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[180px] w-[150px] flex-none rounded-lg" />
          ))}

        {!isLoading &&
          communities.map((c) => (
            <button
              key={c.id}
              onClick={() => open(c.id)}
              aria-label={`Open ${c.name}`}
              className="group relative h-[180px] w-[150px] flex-none overflow-hidden rounded-lg border border-card-line text-left transition-all duration-200 ease-cinematic hover:-translate-y-0.5 hover:border-white/25"
            >
              {c.banner ? (
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-cinematic group-hover:scale-[1.05]"
                  style={{ backgroundImage: `url(${c.banner})` }}
                />
              ) : (
                <div
                  className="absolute inset-0 grid place-items-center"
                  style={{ background: `linear-gradient(150deg, ${c.color}55 0%, transparent 72%)` }}
                >
                  <Users size={34} className="text-white/30" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3 pt-8">
                <div className="flex items-center gap-2">
                  <Avatar src={c.banner} name={c.name} size={20} />
                  <span className="min-w-0 truncate text-[13px] font-bold uppercase tracking-[0.3px] text-white">
                    {c.name}
                  </span>
                </div>
              </div>
            </button>
          ))}

        {/* New community tile — always present, even with no recents. */}
        <button
          onClick={onCreate}
          className="group flex h-[180px] w-[150px] flex-none flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-card-line text-mid transition-all duration-200 ease-cinematic hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.03] hover:text-hi"
        >
          <span className="grid h-10 w-10 place-items-center rounded-full border border-card-line bg-white/[0.04] transition-colors group-hover:border-white/25">
            <Plus size={20} />
          </span>
          <span className="text-[13px] font-semibold">New community</span>
        </button>
      </div>
    </section>
  );
}
